import { AssistantTurn } from "@/components/Answer";
import { QueryInput } from "@/components/QueryInput";
import type { LayoutContext } from "@/components/Layout";
import {
  fetchConversation,
  parseAssistant,
  streamAssistant,
} from "@/lib/api";
import type {
  ConversationDetail,
  ParsedAssistant,
  StoredMessage,
} from "@/lib/types";
import { useEffect, useRef, useState } from "react";
import { useOutletContext, useParams } from "react-router";

type Turn =
  | { kind: "stored"; query: string; parsed: ParsedAssistant }
  | { kind: "live"; query: string; parsed: ParsedAssistant };

function pairsFromStored(messages: StoredMessage[]): Turn[] {
  const turns: Turn[] = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.role !== "User") continue;
    const next = messages[i + 1];
    const parsed: ParsedAssistant =
      next && next.role === "Assistant"
        ? parseAssistant(next.content, true)
        : { answer: "", followUps: [], sources: [], done: false };
    turns.push({ kind: "stored", query: m.content, parsed });
  }
  return turns;
}

export function ConversationPage() {
  const { conversationId } = useParams();
  const { reloadConversations } = useOutletContext<LayoutContext>();
  const [conversation, setConversation] = useState<ConversationDetail | null>(
    null,
  );
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const lastAnswerLen = useRef(0);

  useEffect(() => {
    let mounted = true;
    if (!conversationId) return;
    setConversation(null);
    setTurns([]);
    fetchConversation(conversationId).then((c) => {
      if (!mounted || !c) return;
      setConversation(c);
      setTurns(pairsFromStored(c.messages));
    });
    return () => {
      mounted = false;
    };
  }, [conversationId]);

  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    const onScroll = () => {
      const dist = main.scrollHeight - main.scrollTop - main.clientHeight;
      stickToBottom.current = dist < 120;
    };
    main.addEventListener("scroll", onScroll, { passive: true });
    return () => main.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!streaming) return;
    const last = turns[turns.length - 1];
    const len = last?.parsed.answer.length ?? 0;
    if (len === lastAnswerLen.current) return;
    lastAnswerLen.current = len;
    if (stickToBottom.current) {
      const main = document.querySelector("main");
      main?.scrollTo({ top: main.scrollHeight, behavior: "smooth" });
    }
  }, [turns, streaming]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [conversation?.id]);

  const submit = async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed || streaming || !conversationId) return;
    setInput("");
    const liveTurn: Turn = {
      kind: "live",
      query: trimmed,
      parsed: { answer: "", followUps: [], sources: [], done: false },
    };
    setTurns((t) => [...t, liveTurn]);
    setStreaming(true);
    stickToBottom.current = true;
    lastAnswerLen.current = 0;

    try {
      await streamAssistant({
        endpoint: "/query/followup",
        body: { conversationId, query: trimmed },
        onUpdate: (parsed) => {
          setTurns((t) => {
            const next = [...t];
            const last = next[next.length - 1];
            if (last && last.kind === "live") {
              next[next.length - 1] = {
                kind: "live",
                query: last.query,
                parsed,
              };
            }
            return next;
          });
        },
      });
      reloadConversations();
    } catch (e) {
      console.error(e);
    } finally {
      setStreaming(false);
    }
  };

  if (!conversation) {
    return (
      <div className="mx-auto max-w-3xl px-4 pt-10 pb-32 sm:px-6 sm:pt-16">
        <div className="space-y-6">
          <div className="shimmer h-10 w-3/4 rounded" />
          <div className="space-y-2">
            <div className="shimmer h-3 w-full rounded" />
            <div className="shimmer h-3 w-[88%] rounded" />
            <div className="shimmer h-3 w-[70%] rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <div className="mx-auto w-full max-w-3xl px-4 pt-6 pb-40 sm:px-6 sm:pt-10">
        <div className="mb-6 flex items-center gap-2 text-[10px] tracking-[0.18em] text-muted-foreground/70 uppercase sm:mb-8 sm:text-xs">
          <span className="size-1 rounded-full bg-primary" />
          Thread
        </div>
        <div className="space-y-10 sm:space-y-12">
          {turns.map((t, i) => (
            <AssistantTurn
              key={i}
              query={t.query}
              parsed={t.parsed}
              streaming={
                t.kind === "live" && streaming && i === turns.length - 1
              }
              onPickFollowUp={(q) => submit(q)}
            />
          ))}
        </div>
        <div ref={bottomRef} className="h-2" />
      </div>

      <div className="pointer-events-none sticky bottom-0 left-0 right-0">
        <div className="pointer-events-none h-12 bg-gradient-to-t from-background via-background/80 to-transparent sm:h-16" />
        <div className="pointer-events-auto bg-background/85 backdrop-blur">
          <div className="mx-auto w-full max-w-3xl px-4 pb-4 sm:px-6 sm:pb-6">
            <QueryInput
              value={input}
              onChange={setInput}
              onSubmit={() => submit(input)}
              disabled={streaming}
              variant="compact"
              placeholder={
                streaming ? "Streaming response..." : "Ask follow-up..."
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConversationPage;
